import { API_BASE_URL, apiRequest, getCurrentAccessToken } from '@/shared/lib/api-client';

export interface ContactRow {
  id: number;
  display_name: string | null;
  email: string | null;
  state_uf: string | null;
  profile_pic_url: string | null;
  notes: string | null;
  custom_fields: Record<string, unknown>;
  created_at: string;
  last_inbound_at: string | null;
  primary_identifier: { kind: string; value: string } | null;
  tags: Array<{ id: number; name: string; color: string }>;
  access_groups: Array<{ id: number; name: string }>;
}

export interface ListContactsResponse {
  contacts: ContactRow[];
  next_cursor: string | null;
  /** Total que casa com filtros (q, tag_ids), ignorando cursor. */
  total: number;
}

export function getContact(id: number): Promise<ContactRow> {
  return apiRequest<ContactRow>(`/contacts/${id}`);
}

/**
 * Trigger refresh on-demand da foto de perfil. Backend publica em fila
 * pro worker-whatsmeow re-tentar GetProfilePictureInfo. Async — UI atualiza
 * via realtime `contact.profile_pic.updated` quando worker termina.
 *
 * 400 NO_QR_CHANNEL se contato não tem ticket em canal qr_code.
 */
export function refreshContactProfilePic(id: number): Promise<void> {
  return apiRequest<void>(`/contacts/${id}/refresh-profile-pic`, { method: 'POST' });
}

export function listContacts(input: {
  q?: string | undefined;
  cursor?: string | null | undefined;
  tagIds?: number[] | undefined;
  accessGroupIds?: number[] | undefined;
  limit?: number | undefined;
}): Promise<ListContactsResponse> {
  const params = new URLSearchParams();
  if (input.q) params.set('q', input.q);
  if (input.cursor) params.set('cursor', input.cursor);
  if (input.tagIds && input.tagIds.length > 0) params.set('tag_ids', input.tagIds.join(','));
  if (input.accessGroupIds && input.accessGroupIds.length > 0)
    params.set('access_group_ids', input.accessGroupIds.join(','));
  if (input.limit) params.set('limit', String(input.limit));
  return apiRequest<ListContactsResponse>(`/contacts?${params.toString()}`);
}

export interface CreateContactInput {
  display_name: string;
  phone: string;
  email?: string;
  notes?: string;
  tag_ids?: number[];
  access_group_ids?: number[];
  custom_fields?: Record<string, unknown>;
}

export function createContact(input: CreateContactInput): Promise<{ id: number }> {
  return apiRequest<{ id: number }>('/contacts', { method: 'POST', body: input });
}

/**
 * Atualiza e DEVOLVE o contato já atualizado — mesmo shape do
 * `GET /contacts/:id` (o servidor usa a mesma função).
 *
 * Antes respondia vazio, e quem editava tinha que perguntar em seguida "e como
 * ficou?", numa segunda ida ao servidor. Quem só quer saber se deu certo pode
 * continuar ignorando o retorno.
 */
export function updateContact(
  id: number,
  input: Partial<CreateContactInput>,
): Promise<ContactRow> {
  return apiRequest<ContactRow>(`/contacts/${id}`, { method: 'PATCH', body: input });
}

export function mergeContacts(input: {
  source_contact_id: number;
  target_contact_id: number;
}): Promise<{ moved: number }> {
  return apiRequest<{ moved: number }>('/contacts/merge', { method: 'POST', body: input });
}

export function bulkDeleteContacts(input: {
  contact_ids: number[];
  confirmation: 'APAGAR';
}): Promise<{ deleted: number; failed: number[] }> {
  return apiRequest<{ deleted: number; failed: number[] }>('/contacts/bulk-delete', {
    method: 'POST',
    body: input,
  });
}

/** Apaga TODOS os contatos do tenant (admin). Retorna quantos hard/soft-deletados. */
export function deleteAllContacts(input: {
  confirmation: 'APAGAR';
}): Promise<{ deleted: number; soft_deleted: number }> {
  return apiRequest<{ deleted: number; soft_deleted: number }>('/contacts/delete-all', {
    method: 'POST',
    body: input,
  });
}

export interface BulkImportRow {
  display_name: string;
  phone: string;
  email?: string;
  custom_fields?: Record<string, unknown>;
}

export function bulkImportContacts(input: {
  rows: BulkImportRow[];
  /** Etiquetas a vincular a todos os contatos importados (opcional). */
  tag_ids?: number[];
  /** Grupos de acesso (empresa) a vincular a todos os contatos (opcional). */
  access_group_ids?: number[];
}): Promise<{
  succeeded: number;
  failed: Array<{ row: number; error: string }>;
}> {
  return apiRequest<{ succeeded: number; failed: Array<{ row: number; error: string }> }>(
    '/contacts/bulk-import',
    { method: 'POST', body: input },
  );
}

export async function downloadContactsExport(format: 'csv' | 'xlsx'): Promise<void> {
  const token = getCurrentAccessToken();
  const res = await fetch(`${API_BASE_URL}/contacts/export?format=${format}`, {
    credentials: 'include',
    ...(token && { headers: { authorization: `Bearer ${token}` } }),
  });
  if (!res.ok) throw new Error(`export failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `contacts.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
